import { Component, effect, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { DirectLineService } from './services/directline.service';
import { SCOUT_AI_MESSAGES } from './models/directline.models';
import { ScoutAiSessionComponent } from './scout-ai-session.component';
import { ScoutAiTriggerService } from './services/scout-ai-trigger.service';

@Component({
    selector: 'app-scout-ai-drawer',
    standalone: false,
    templateUrl: './scout-ai-drawer.component.html',
    styleUrls: ['./scout-ai-drawer.component.css', './scout-ai-shared.css']
})
export class ScoutAiDrawerComponent implements OnInit, OnDestroy {

    public directLineService = inject(DirectLineService);
    private triggerService = inject(ScoutAiTriggerService);

    @ViewChild(ScoutAiSessionComponent) private session?: ScoutAiSessionComponent;

    isOpen = signal(false);
    isMobile = signal(false);
    currentDrawerWidth = signal(500);

    private openRequestCount = 0;
    private triggerEffect = effect(() => {
        const count = this.triggerService.openRequested();
        if (count !== this.openRequestCount) {
            this.openRequestCount = count;
            if (count > 0 && !this.isMobile()) {
                this.open();
            }
        }
    });

    private popoutWindow: Window | null = null;
    private isResizing: boolean = false;
    private startX: number = 0;
    private startWidth: number = 0;

    private readonly MIN_WIDTH = 300;
    private readonly MAX_WIDTH_PERCENT = 0.8;
    private readonly DEFAULT_WIDTH = 500;
    private readonly MOBILE_BREAKPOINT = 768;
    private readonly VALID_MESSAGE_TYPES = [SCOUT_AI_MESSAGES.DOCK];

    ngOnInit() {
        this.checkIfMobile();
        window.addEventListener('resize', this.checkIfMobile);
        window.addEventListener('message', this.handleMessage);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    private handleBeforeUnload = (): void => {
        this.directLineService.storeSessionImmediate();
        this.closePopoutWindow();
    }

    private handleMessage = (event: MessageEvent): void => {
        if (event.origin !== window.location.origin) return;
        if (!event.data || typeof event.data !== 'object') return;
        if (typeof event.data.type !== 'string') return;
        if (!this.VALID_MESSAGE_TYPES.includes(event.data.type)) return;

        if (event.data.type === SCOUT_AI_MESSAGES.DOCK) {
            this.open();
        }
    }

    private checkIfMobile = (): void => {
        const wasMobile = this.isMobile();
        const nowMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;
        this.isMobile.set(nowMobile);
        if (!wasMobile && nowMobile && this.directLineService.connectionStatus() !== 'disconnected') {
            this.directLineService.disconnect();
            this.isOpen.set(false);
        }
    }

    private closePopoutWindow(): void {
        if (this.popoutWindow && !this.popoutWindow.closed) {
            this.popoutWindow.postMessage({ type: SCOUT_AI_MESSAGES.CLOSE }, window.location.origin);
            this.popoutWindow = null;
        }
    }

    open(): void {
        this.closePopoutWindow();
        this.isOpen.set(true);
        this.currentDrawerWidth.set(this.DEFAULT_WIDTH);
        document.body.style.overflow = 'hidden';
        this.session?.connect();
    }

    startResize(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.startX = event.clientX;
        this.startWidth = this.currentDrawerWidth();
        this.isResizing = true;

        document.addEventListener('mousemove', this.onResize);
        document.addEventListener('mouseup', this.stopResize);

        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
    }

    private onResize = (event: MouseEvent): void => {
        if (!this.isResizing) return;
        const deltaX = this.startX - event.clientX;
        let newWidth = this.startWidth + deltaX;
        const maxWidth = window.innerWidth * this.MAX_WIDTH_PERCENT;
        newWidth = Math.max(this.MIN_WIDTH, Math.min(newWidth, maxWidth));
        this.currentDrawerWidth.set(newWidth);
    }

    private stopResize = (): void => {
        if (!this.isResizing) return;
        this.isResizing = false;
        document.removeEventListener('mousemove', this.onResize);
        document.removeEventListener('mouseup', this.stopResize);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }

    close() {
        this.stopResize();
        this.isOpen.set(false);
        document.body.style.overflow = '';
    }

    popOut(): void {
        const width = 420;
        const height = 650;
        const left = (screen.width - width) / 2;
        const top = (screen.height - height) / 2;
        const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`;
        const baseUrl = window.location.origin + window.location.pathname;
        const popoutUrl = `${baseUrl}#/scout-ai-popout`;

        this.directLineService.storeSessionImmediate();
        this.directLineService.disconnect();

        this.popoutWindow = window.open(popoutUrl, 'ScoutAIAssistant', features);

        if (this.popoutWindow) {
            this.close();
        }
    }

    toggle() {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    ngOnDestroy() {
        window.removeEventListener('resize', this.checkIfMobile);
        window.removeEventListener('message', this.handleMessage);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this.directLineService.disconnect();
        this.stopResize();
    }
}
